import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import { AutoComplete, Modal, notification } from 'antd'

import type { WasteCarrier } from 'portal/store/interfaces'
import { colours } from '@qflow/theme'

import { useModuleIds } from 'portal/lib/hooks'
import { ColourButton } from 'portal/lib/primitives'
import { LabeledValue } from 'portal/lib/sharedComponents/Select'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { carriersLoad } from 'portal/store/wasteReferenceData/carriers/actions'
import { InfractionFieldMessageMapper } from './UpdateSectionHelpers'
import { recordBulkUpdate } from 'portal/store/records/bulkUpdate/actions'

export function UpdateSection({
  onApply,
  eventCount,
  recordIds,
  recordCount,
  selectedEventKeys,
  eventRecordMapping
}: UpdateSectionProps) {
  const [relatedField, setRelatedField] = useState<string>('')
  const [newValue, setNewValue] = useState<string>('')
  const [correspondingCarrierName, setCorrespondingCarrierName] =
    useState<string>('')
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const showConfirmationModal = () => {
    setIsConfirmationModalOpen(true)
  }
  const handleConfirmationOk = () => {
    dispatch(
      trackMixpanel('frontend_event_update_section_confirm_ok', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds
      })
    )
    setIsConfirmationModalOpen(false)
    onCarrierLicenceUpdate()
    onApply()
  }
  const handleConfirmationCancel = () => {
    dispatch(
      trackMixpanel('frontend_event_update_section_confirm_cancel', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds
      })
    )
    setIsConfirmationModalOpen(false)
  }

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()

  const {
    carriers,
    error
  }: {
    carriers: WasteCarrier[]
    error: any
  } = useSelector((state: any) => state.carriers)

  useEffect(() => {
    dispatch(
      trackMixpanel('frontend_event_update_section_open', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds
      })
    )
  }, [accountId, dispatch, moduleName, projectId, recordIds])

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  useEffect(() => {
    dispatch(carriersLoad(newValue))
  }, [dispatch, newValue])

  const options = useMemo(() => {
    if (!carriers?.length) {
      return []
    }

    return carriers.map<LabeledValue>(carrier => {
      return {
        value: carrier.carrierLicenceId,
        label: `${carrier.carrierLicenceId} (${carrier.name})`
      }
    })
  }, [carriers])

  const hasDifferentDescriptions: boolean = useMemo(() => {
    // If there are no selected alerts, return false
    if (selectedEventKeys.length === 0) {
      return false
    }

    // set the related field based on the first selected alert
    setRelatedField(
      InfractionFieldMessageMapper[
        eventRecordMapping[selectedEventKeys[0]]?.description || ''
      ]
    )

    if (selectedEventKeys.length < 2) {
      return false
    }
    // Check if all selected alerts have the same description
    const descriptions = selectedEventKeys.map(
      key => eventRecordMapping[key]?.description
    )
    return new Set(descriptions).size > 1
  }, [eventRecordMapping, selectedEventKeys])

  const hasDifferentSubcontractors: boolean = useMemo(() => {
    if (selectedEventKeys.length < 2) {
      return false
    }
    // Check if all selected alerts have the same subcontractor (ignoring blank values)
    const subcontractors = selectedEventKeys
      .filter(
        key =>
          eventRecordMapping[key]?.subcontractor !== '' &&
          eventRecordMapping[key]?.subcontractor !== undefined &&
          eventRecordMapping[key]?.subcontractor !== null
      )
      .map(key => eventRecordMapping[key]?.subcontractor)

    // get the unique subcontractors
    const uniqueSubcontractors = new Set(subcontractors)

    // Return true if there are multiple subcontractors
    return uniqueSubcontractors.size > 1
  }, [eventRecordMapping, selectedEventKeys])

  const hasDifferentWasteCarriers: boolean = useMemo(() => {
    if (
      selectedEventKeys.length < 2 &&
      eventRecordMapping[selectedEventKeys[0]]?.wasteCarrier
    ) {
      return false
    }
    // Check if all selected alerts have the same carrier (ignoring blank values)
    const carriers = selectedEventKeys
      .filter(
        key =>
          eventRecordMapping[key]?.wasteCarrier !== '' &&
          eventRecordMapping[key]?.wasteCarrier !== undefined &&
          eventRecordMapping[key]?.wasteCarrier !== null
      )
      .map(key => eventRecordMapping[key]?.wasteCarrier)

    // get the unique carriers
    const uniqueCarriers = new Set(carriers)

    // Return true if there are multiple carriers
    return uniqueCarriers.size > 1
  }, [eventRecordMapping, selectedEventKeys])

  const handleOnChange = (value: string) => {
    setNewValue(value)
    // Find the corresponding carrier name based on the selected value
    const selectedCarrier = carriers.find(
      carrier => carrier.carrierLicenceId === value
    )
    if (selectedCarrier) {
      setCorrespondingCarrierName(selectedCarrier.name)
    } else {
      setCorrespondingCarrierName('')
    }
  }

  const showSuccessNotification = response => {
    notification.success({
      message: response,
      duration: 5,
      placement: 'top'
    })
  }

  const showFailureNotification = () => {
    notification.error({
      message: 'Records failed to be updated.',
      description: 'Please reload the page and try again.',
      duration: 10,
      placement: 'top'
    })
  }

  const onCarrierLicenceUpdate = () => {
    // If the related field is not set, return early
    if (!relatedField) {
      return
    }

    // If the new value is not set, return early
    if (newValue === '') {
      return
    }

    // Dispatch the action to update the records
    dispatch(
      recordBulkUpdate(
        accountId as string,
        projectId as string,
        moduleName,
        relatedField,
        newValue,
        recordIds,
        showSuccessNotification,
        showFailureNotification
      )
    )
  }

  // RETURNS
  if (hasDifferentDescriptions || hasDifferentWasteCarriers) {
    return (
      <StyledUpdateSection>
        <ErrorMessage>
          To bulk update edit your selections so they have the same:
          <ul>
            <li className={hasDifferentDescriptions ? 'error' : ''}>
              Description
            </li>
            <li className={hasDifferentWasteCarriers ? 'error' : ''}>
              Carrier
            </li>
          </ul>
        </ErrorMessage>
      </StyledUpdateSection>
    )
  }

  if (relatedField === 'carrierLicenceId') {
    return (
      <>
        <StyledUpdateSection>
          <h3>Set Carrier Licence</h3>

          <FieldInput>
            <span>Carrier licence</span>

            <AutoComplete
              allowClear
              placeholder="Enter a licence"
              value={newValue}
              options={options}
              popupMatchSelectWidth={false}
              onChange={handleOnChange}
              style={{ width: '54%' }}
            />
          </FieldInput>

          {newValue && (
            <FieldInput>
              <span>Carrier name</span>
              <span>
                {correspondingCarrierName ? (
                  <span style={{ paddingLeft: '0.75rem' }}>
                    {correspondingCarrierName}
                  </span>
                ) : newValue ? (
                  <EventUpdateMessage
                    style={{
                      width: '280px',
                      padding: '0.25rem 0.5rem 0.25rem 1.75rem',
                      backgroundPosition: '8px 7px'
                    }}
                  >
                    Carrier name not found in EA data
                  </EventUpdateMessage>
                ) : (
                  ''
                )}
              </span>
            </FieldInput>
          )}
          {hasDifferentSubcontractors && (
            <EventUpdateMessage style={{ width: '518px' }}>
              Warning: The selected alerts have different 'Subcontractors'.
            </EventUpdateMessage>
          )}
          <EventUpdateMessage style={{ width: '518px' }}>
            Warning: Changing the 'Carrier licence' will cause all Carrier
            information to be updated.
          </EventUpdateMessage>

          <ButtonWrapper>
            <ColourButton.Action
              type="button"
              onClick={showConfirmationModal}
              disabled={newValue === ''}
            >
              Apply to {eventCount} alert{eventCount > 1 && 's'}
            </ColourButton.Action>
          </ButtonWrapper>
        </StyledUpdateSection>
        <Modal
          title="Updating records"
          open={isConfirmationModalOpen}
          onOk={handleConfirmationOk}
          onCancel={handleConfirmationCancel}
          transitionName=""
          footer={[
            <ColourButton.Basic
              key="back"
              type="button"
              onClick={handleConfirmationCancel}
              style={{ display: 'inline-block', marginRight: '.75rem' }}
            >
              Cancel
            </ColourButton.Basic>,
            <ColourButton.Action
              key="submit"
              type="button"
              onClick={handleConfirmationOk}
              style={{ display: 'inline-block' }}
            >
              OK
            </ColourButton.Action>
          ]}
        >
          <p>
            The {eventCount} selected alert{eventCount > 1 && 's'} relate
            {eventCount === 1 && 's'} to {recordCount} record
            {recordCount > 1 && 's'}.
          </p>

          <p>
            <LabelSpan>Carrier licence</LabelSpan> <b>{newValue}</b>
            <br />
            <LabelSpan>Carrier name</LabelSpan>{' '}
            <b>{correspondingCarrierName}</b>
          </p>

          <EventUpdateMessage style={{ marginBottom: '1rem' }}>
            Please note that it may take a few minutes for the related alerts to
            be updated.
          </EventUpdateMessage>
        </Modal>
      </>
    )
  }

  return (
    <StyledUpdateSection>
      <div>
        Bulk updates are only available for 'No waste carrier licence' alerts.
      </div>
    </StyledUpdateSection>
  )
}

type UpdateSectionProps = {
  onApply: () => void
  eventCount: number
  recordIds: string[]
  recordCount: number
  selectedEventKeys: string[]
  eventRecordMapping: any
}

const StyledUpdateSection = styled.div`
  padding: 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 5px;
  margin: 1rem 0 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;

  h3 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
  }
`
const ErrorMessage = styled.div`
  color: ${colours.ERROR};
  font-weight: 600;
  margin: 0 auto;

  ul {
    color: ${colours.CORE_NAVY};
    list-style: none;
    margin: 0.5rem 0 0 0;
    padding-left: 4rem;

    li {
      margin: 0.25rem 0;
    }
    li:before {
      color: ${colours.SUCCESS};
      content: '✓';
      padding-right: 0.5rem;
    }
    li.error {
      &:before {
        color: ${colours.ERROR};
        content: '✗';
      }
    }
  }
`
const FieldInput = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  > span {
    min-width: 6.5rem;
  }
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;

  button {
    width: fit-content;
  }
`
export const EventUpdateMessage = styled.div`
  color: ${colours.ORANGE_TEXT};
  font-size: 13px;
  font-weight: 500;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0.583008 12.5418H13.4163L6.99967 1.4585L0.583008 12.5418ZM7.58301 10.7918H6.41634V9.62516H7.58301V10.7918ZM7.58301 8.4585H6.41634V6.12516H7.58301V8.4585Z" fill="%23DA5E30" /></svg>');
  background-position: 8px 12px;
  background-repeat: no-repeat;
  margin: 0;
  width: 100%;
  padding: 0.5rem 0.625rem 0.5rem 1.75rem;
  border: 1px solid ${colours.ORANGE};
  border-radius: 5px;
  background-color: #fef7f3;
`
const LabelSpan = styled.span`
  min-width: 7.5rem;
  display: inline-block;
`
